<script>
import { layoutMethods, smsBlastCampaignMethods } from "@/state/helpers";
import { required } from "vuelidate/lib/validators";
import Swal from "sweetalert2";
import flatPickr from "vue-flatpickr-component";
import moment from 'moment-timezone';
import * as XLSX from 'xlsx';
import "flatpickr/dist/flatpickr.css";
import feather from 'feather-icons';

export default {
  page: {
    title: "Add SMS Campaign",
  },
  validations: {
    campaign: {
      name: { required },
      scheduleDate: { required },
      senderName: { required },
      message: { required },
    },
  },
  data() {
    return {
      submitted: false,
      campaign: {
        id: null,
        name: "",
        scheduleDate: "",
        senderName: "",
        message: "",
      },
      massUploadFile: null,
      mobileNumbers: [""],
      searchMobileNumber: "",
      mobileNumbersValidation: {
        hasError: false,
        errorMessage: "",
      },
      dateConfig: {
        allowInput: true,
        enableTime: true,
        minDate: "today",
        dateFormat: "Y-m-d H:i",
      },
      senderNameOptions: [],
    };
  },
  components: {
    flatPickr,
  },
  computed: {
    totalMobileNumbers() {
      return this.mobileNumbers.filter(number => 
        number.trim() !== "" && this.isValidPhoneNumber(number)
      ).length;
    },
    totalInvalidNumbers() {
      return this.mobileNumbers.filter(number => 
        number.trim() !== "" && !this.isValidPhoneNumber(number)
      ).length;
    },
    totalDuplicateNumbers() {
      const nonEmptyNumbers = this.mobileNumbers.filter(number => number.trim() !== "");
      const uniqueNumbers = [...new Set(nonEmptyNumbers)];
      return nonEmptyNumbers.length - uniqueNumbers.length;
    },
    filteredMobileNumbers() {
      if (!this.searchMobileNumber.trim()) {
        return this.mobileNumbers.map((number, index) => ({ number, index }));
      }
      return this.mobileNumbers
        .map((number, index) => ({ number, index }))
        .filter(item => 
          item.number.toLowerCase().includes(this.searchMobileNumber.toLowerCase())
        );
    }
  },
  methods: {
    ...layoutMethods,
    ...smsBlastCampaignMethods,
    async saveCampaign(isDraft = true) {
      this.submitted = true;
      this.$v.$touch();

      // Validate mobile numbers and set error state
      this.validateMobileNumbers();

      // Check if form fields are valid and mobile numbers are valid
      if (this.$v.$invalid || this.mobileNumbersValidation.hasError) {
        return;
      }

      // Prepare payload
      const payload = {
        campaign_name: this.campaign.name,
        schedule_date: this.campaign.scheduleDate, 
        sender_name: this.campaign.senderName,
        message: this.campaign.message,
        is_draft: isDraft ? 1 : 0,
        recipients: this.mobileNumbers.filter(number => 
          number.trim() !== "" && this.isValidPhoneNumber(number)
        ),
      };

      const isUpdate = this.$route.params.id > 0;
      const action = isUpdate ? 'update' : 'create';
      
      if (isUpdate) {
        payload.id = this.campaign.id;
      }

      try {
        if (isDraft) {
          // For drafts: save silently and go back
          await this.saveSilently(payload, action);
        } else {
          // For final save: show confirmation dialog
          await this.saveWithConfirmation(payload, action, isUpdate);
        }
      } catch (error) {
        const errorMsg = isDraft 
          ? "There was an error saving the draft." 
          : "There was an error saving the campaign.";
        Swal.fire("Error", errorMsg, "error");
      }
    },

    async saveSilently(payload, action) {
      const response = await this.actionSMSBlastAPIPOST({
        params: payload,
        action: action
      });

      const { status, message } = response;
      if (status !== 'ok') {
        throw new Error(message || 'Save failed');
      }

      // Go back to previous page without any alerts
      this.$router.go(-1);
    },

    async saveWithConfirmation(payload, action, isUpdate) {
      const confirmConfig = {
        title: "Are you sure?",
        text: isUpdate 
          ? "You will no longer be able to edit this request" 
          : `You are about to create ${this.campaign.name}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: isUpdate ? "Yes, Update" : "Yes, Create",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#e73092",
        cancelButtonColor: "#6c757d",
      };

      const result = await Swal.fire(confirmConfig);
      
      if (!result.isConfirmed) {
        return;
      }

      const response = await this.actionSMSBlastAPIPOST({
        params: payload,
        action: action
      });

      console.log("Save Response:", response);

      const { status, message } = response;
      if (status !== 'ok') {
        await Swal.fire({
          icon: 'warning',
          title: 'Create Failed',
          text: message,
          confirmButtonText: 'OK',
          confirmButtonColor: '#e73092'
        });
        return;
      }

      // Show success message
      const successConfig = {
        title: isUpdate ? "Successfully Updated!" : "Successfully Created!",
        text: isUpdate 
          ? `${this.campaign.name} has been updated.` 
          : `${this.campaign.name} has been created.`,
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#e73092"
      };

      const successResult = await Swal.fire(successConfig);
      if (successResult.isConfirmed) {
        this.$router.push({ name: "SMS Blast Campaign" });
      }
    },

    async saveDraft() {
      // Save as draft logic here
      await this.saveCampaign(true);
    },

    validateMobileNumbers() {
      const validMobileNumbers = this.mobileNumbers.filter(number => 
        number.trim() !== "" && this.isValidPhoneNumber(number)
      );
      
      const hasEmptyNumbers = this.mobileNumbers.some(number => number.trim() === "");
      const hasInvalidNumbers = this.totalInvalidNumbers > 0;
      const hasDuplicateNumbers = this.totalDuplicateNumbers > 0;

      // Reset validation state
      this.mobileNumbersValidation.hasError = false;
      this.mobileNumbersValidation.errorMessage = "";

      if (validMobileNumbers.length === 0) {
        this.mobileNumbersValidation.hasError = true;
        this.mobileNumbersValidation.errorMessage = "Please add at least one valid mobile number.";
        return;
      }

      if (hasEmptyNumbers) {
        this.mobileNumbersValidation.hasError = true;
        this.mobileNumbersValidation.errorMessage = "Please remove empty mobile number fields or fill them with valid numbers.";
        return;
      }

      if (hasInvalidNumbers) {
        this.mobileNumbersValidation.hasError = true;
        this.mobileNumbersValidation.errorMessage = "Please fix all invalid mobile numbers before saving.";
        return;
      }

      if (hasDuplicateNumbers) {
        this.mobileNumbersValidation.hasError = true;
        this.mobileNumbersValidation.errorMessage = "Please remove duplicate mobile numbers before saving.";
        return;
      }
    },

    addMobileNumberRow() {
      this.mobileNumbers.push("");
      // Focus on the newly created input field
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll('.mobile-number-input');
        const lastInput = inputs[inputs.length - 1];
        if (lastInput) {
          lastInput.focus();
        }
      });
    },

    removeMobileNumberRow(index) {
      if (this.mobileNumbers.length > 1) {
        this.mobileNumbers.splice(index, 1);
        // Clear search if the list becomes empty after removal
        if (this.mobileNumbers.filter(n => n.trim() !== "").length === 0) {
          this.searchMobileNumber = "";
        }
      }
    },

    // Validate and format phone number input
    validatePhoneInput(event, index) {
      let value = event.target.value;
      
      // Remove all non-numeric characters except + at the beginning
      value = value.replace(/[^\d+]/g, '');
      
      // Ensure + only appears at the beginning
      if (value.includes('+')) {
        const plusIndex = value.indexOf('+');
        if (plusIndex > 0) {
          value = value.replace(/\+/g, '');
        } else {
          value = '+' + value.substring(1).replace(/\+/g, '');
        }
      }
      
      if (value.startsWith('+')) {
        value = value.substring(0, 16); // +15 digits
      } else {
        value = value.substring(0, 15); // 15 digits
      }
      
      // Update the model
      this.$set(this.mobileNumbers, index, value);
      
      // Clear validation error when user starts typing
      if (this.mobileNumbersValidation.hasError) {
        this.mobileNumbersValidation.hasError = false;
        this.mobileNumbersValidation.errorMessage = "";
      }
    },

    // Check if phone number is duplicate
    isDuplicatePhoneNumber(phoneNumber, currentIndex) {
      if (!phoneNumber || phoneNumber.trim() === '') return false;
      
      return this.mobileNumbers.some((number, index) => 
        index !== currentIndex && 
        number.trim() !== '' && 
        number.trim() === phoneNumber.trim()
      );
    },

    // Check if phone number is valid
    isValidPhoneNumber(phoneNumber) {
      if (!phoneNumber || phoneNumber.trim() === '') return true; // Allow empty
      
      // Philippine mobile number patterns
      const philippinePatterns = [
        /^09\d{9}$/,           // 09XXXXXXXXX (11 digits)
        /^\+639\d{9}$/,        // +639XXXXXXXXX (13 characters)
        /^639\d{9}$/,          // 639XXXXXXXXX (12 digits)
      ];
      
      // International format (basic validation)
      const internationalPattern = /^\+\d{7,15}$/;
      
      return philippinePatterns.some(pattern => pattern.test(phoneNumber)) || 
             internationalPattern.test(phoneNumber);
    },

    // Get validation class for input
    getPhoneValidationClass(phoneNumber, index) {
      if (!phoneNumber || phoneNumber.trim() === '') return '';
      
      if (this.isDuplicatePhoneNumber(phoneNumber, index)) {
        return 'is-invalid';
      }
      
      return this.isValidPhoneNumber(phoneNumber) ? 'is-valid' : 'is-invalid';
    },

    // Method to add sample data for testing
    addSampleData() {
      this.mobileNumbers = [
        "09123456789",
        "09423345467", 
        "09423345467",
        "09423345467",
        "09423345467",
        "09198765432",
        "09276543210",
        "09334567890",
        "09445678901",
        "09556789012"
      ];
    },

    onMassUploadFileChange(event) {
      this.massUploadFile = event.target.files[0];
    },

    async processMassUploadFile() {
      if (!this.massUploadFile) {
        Swal.fire("Error", "Please select a file to upload", "error");
        return;
      }

      // Check file type
      const fileName = this.massUploadFile.name.toLowerCase();
      const isValidFileType = fileName.endsWith('.csv') || 
                             fileName.endsWith('.xlsx') || 
                             fileName.endsWith('.xls');

      if (!isValidFileType) {
        Swal.fire("Invalid File!", "Please upload a CSV or Excel file (.csv, .xlsx, .xls)", "error");
        // Reset file input
        this.massUploadFile = null;
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
        }
        return;
      }

      try {
        let mobileNumbers = [];

        if (fileName.endsWith('.csv')) {
          mobileNumbers = await this.parseCSVFile(this.massUploadFile);
        } else {
          mobileNumbers = await this.parseExcelFile(this.massUploadFile);
        }

        // Check if file is empty
        if (mobileNumbers.length === 0) {
          Swal.fire("Invalid File!", "You added an empty file.", "error");
          // Reset file input
          this.resetFileUpload();
          return;
        }

        // Process and validate mobile numbers
        this.processExtractedMobileNumbers(mobileNumbers);

      } catch (error) {
        console.error("Error processing file:", error);
        Swal.fire("Error", "There was an error processing the file", "error");
      } 
    },
    resetFileUpload() {
      // Reset the file input after processing (success or error)
        this.massUploadFile = null;
        // Also reset the file input element
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
        }
    },

    async parseCSVFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text = e.target.result;
            const lines = text.split('\n').filter(line => line.trim() !== '');
            
            if (lines.length === 0) {
              resolve([]);
              return;
            }

            const mobileNumbers = [];
            
            // Check if first line is header 
            const firstLine = lines[0].toLowerCase();
            const hasHeader = firstLine.toString().toLowerCase().includes('mobile number');
            const startIndex = hasHeader ? 1 : 0;

            for (let i = startIndex; i < lines.length; i++) {
              const columns = lines[i].split(',');
              
              // Try to find mobile number in any column
              for (let col of columns) {
                const cleanedNumber = col.trim().replace(/['"]/g, '');
                if (cleanedNumber && cleanedNumber.trim() !== '') {
                  mobileNumbers.push(cleanedNumber);
                  break; // Only take first non-empty value per row
                }
              }
            }

            resolve(mobileNumbers);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
    },

    async parseExcelFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length === 0) {
              resolve([]);
              return;
            }

            const mobileNumbers = [];
            
            // Check if first row is header
            const firstRow = jsonData[0];
            // console.log("First Row:", firstRow);
            const hasHeader = firstRow.some(cell => 
              cell && cell.toString().toLowerCase().includes('mobile number')
            );
            const startIndex = hasHeader ? 1 : 0;

            for (let i = startIndex; i < jsonData.length; i++) {
              const row = jsonData[i];
              
              // Try to find mobile number in any column
              for (let cell of row) {
                if (cell) {
                  const cleanedNumber = cell.toString().trim();
                  if (cleanedNumber && cleanedNumber.trim() !== '') {
                    mobileNumbers.push(cleanedNumber);
                    break; // Only take first non-empty value per row
                  }
                }
              }
            }

            resolve(mobileNumbers);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
      });
    },

    processExtractedMobileNumbers(extractedNumbers) {
      // Remove duplicates and track them
      const uniqueNumbers = [];
      const duplicates = [];
      const invalid = [];

      extractedNumbers.forEach(number => {
        const cleanNumber = number.trim();
        
        if (!this.isValidPhoneNumber(cleanNumber)) {
          invalid.push(cleanNumber);
        } else if (uniqueNumbers.includes(cleanNumber)) {
          duplicates.push(cleanNumber);
        } else {
          uniqueNumbers.push(cleanNumber);
        }
      });

      // Check for duplicates with existing mobile numbers
      const existingDuplicates = [];
      uniqueNumbers.forEach(number => {
        if (this.mobileNumbers.includes(number)) {
          existingDuplicates.push(number);
        }
      });

      const allDuplicates = [...duplicates, ...existingDuplicates];
      const hasIssues = allDuplicates.length > 0 || invalid.length > 0;

      if (hasIssues) {
        this.showValidationModal(uniqueNumbers, allDuplicates, invalid);
      } else {
        // All valid, add to mobile numbers
        this.mobileNumbers = [...this.mobileNumbers.filter(num => num.trim() !== ''), ...uniqueNumbers];
        Swal.fire({
          icon: 'success',
          title: 'Successfully Uploaded!',
          text: 'File has been uploaded.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e73092'
        });
        this.resetFileUpload();
      }
    },

    showValidationModal(validNumbers, duplicates, invalid) {
      const issues = [];
      
      // Add duplicates to issues list
      duplicates.forEach(number => {
        issues.push({ number, note: 'duplicate' });
      });
      
      // Add invalid numbers to issues list
      invalid.forEach(number => {
        issues.push({ number, note: 'invalid' });
      });
      let errorText = ''
      if(duplicates.length>0){
        errorText = 'Duplicate Data'
      }
      if(invalid.length>0){
        errorText = 'Invalid Data'
      }
      if(duplicates.length>0 && invalid.length>0){
        errorText = 'Duplicate & Invalid Data'
      }

      let tableRows = '';
      issues.forEach(issue => {
        tableRows += `
          <tr>
            <td style="padding: 8px; text-align: left;">${issue.number}</td>
            <td style="padding: 8px; text-align: center;">${issue.note}</td>
          </tr>
        `;
      });

      Swal.fire({
        icon: 'error',
        title: 'Invalid File!',
        html: `
          <div style="text-align: center; font-family: Arial, sans-serif;">
            <p>You added an invalid data and the file contained duplicate data.</p>
            <p style="color: #e73092;font-weight: 300; margin: 15px 0px 0px 0px; text-align: left; font-size: 14px;">
              ${errorText} will be remove once you select to proceed.
            </p>

            <div style="max-height: 200px; overflow-y: auto; margin: 15px 0; border: 1px solid #ccc; border-radius: 8px;">
              <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
                <thead>
                  <tr style="background-color: #f8f9fa;">
                    <th style="position:sticky;top:0;z-index:2;background:#f8f9fa;padding:10px;border-bottom:1px solid #dee2e6;text-align:left;">Mobile Number</th>
                    <th style="position:sticky;top:0;z-index:2;background:#f8f9fa;padding:10px;border-bottom:1px solid #dee2e6;text-align:left;">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  ${tableRows}
                </tbody>
              </table>
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Proceed',
        cancelButtonText: 'Close',
        confirmButtonColor: '#e73092',
        cancelButtonColor: '#6c757d',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          // Remove empty strings and add only valid unique numbers
          const filteredExisting = this.mobileNumbers.filter(num => num.trim() !== '');
          const newValidNumbers = validNumbers.filter(num => !filteredExisting.includes(num));
          this.mobileNumbers = [...filteredExisting, ...newValidNumbers];
          
          if(newValidNumbers.length > 0)
          {
            Swal.fire({
              icon: 'success',
              title: 'Successfully Uploaded!',
              text: 'File has been uploaded.',
              confirmButtonText: 'OK',
              confirmButtonColor: '#e73092'
            });
            this.resetFileUpload();
          }
          else {
            Swal.fire({
              icon: 'info',
              title: 'No New Numbers Added',
              text: 'All numbers in the file were either duplicates or invalid.',
              confirmButtonText: 'OK',
              confirmButtonColor: '#e73092'
            });
          }
          
        }
      });
    },
  },

  async mounted() {
    this.changeTitle({ title: "Add SMS Campaign" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr", to: { name: "Home" } },
        { text: "Notification", to: { name: "" } },
        { text: "SMS Blast", to: { name: "SMS Blast Campaign" } },
        { text: "Add SMS Campaign", to: { name: "" } },
      ],
    });

    // Add sample data for testing the scroll and search functionality
    // this.addSampleData();

    await this.actionSMSBlastAPIPOST({
      params: {},
      action: 'fetch-sms-sender-names'
    }).then((res) => {
      // console.log("Loaded Sender Names:", res);
      const {status, message} = res;
      if(status != 'ok'){
        Swal.fire("Error", message || "There was an error loading the sender names", "error");
        return;
      }
      // Populate sender name options
      if(Array.isArray(message) && message.length > 0){
        this.senderNameOptions = message.map(name => ({ text: name, value: name }));
      }
    }).catch((err) => {
      console.error("Error loading sender names:", err);
      Swal.fire("Error", "There was an error loading the sender names", "error");
      return;
    });

    if (this.$route.params.id > 0) {
      // Load existing campaign data for editing
      await this.actionSMSBlastAPIPOST({
        params: { id: this.$route.params.id },
        action: 'details'
      }).then((res) => {
        // console.log("Loaded SMS Campaign for Editing:", res);
        const {status, message} = res;
        if(status != 'ok'){
          Swal.fire("Error", message || "There was an error loading the campaign data", "error");
          return;
        }
        // Populate form with existing data
        this.campaign.id = message.id;
        this.campaign.name = message.campaignName || "";
        this.campaign.scheduleDate = moment.tz(message.scheduledDate, 'Asia/Manila').format('YYYY-MM-DD HH:mm:ss') || "";
        this.campaign.senderName = message.senderName || "";
        this.campaign.message = message.message || "";
        this.mobileNumbers = message.recipients?.rows && message.recipients?.rows.length > 0 ? message.recipients.rows.map(item => item.number) : [""];
      }).catch((err) => {
        console.error("Error loading SMS Campaign:", err);
        Swal.fire("Error", "There was an error loading the campaign data", "error");
        return;
      });

      
    }
    this.$nextTick(() => {
      feather.replace({ width: 18, height: 18 });
    });
  },
};
</script>
<template>
  <div>
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <h3>SMS Blast</h3>
            <p>Send marketing messages to your users or by CSV upload</p>
            
            <div class="row">
              <div class="mb-3 col-md-6">
                <div class="d-flex align-items-center">
                  <label class="form-label small fw-bold mb-0">Campaign Name <span class="text-danger">*</span></label>
                </div>
                <input
                  class="form-control"
                  :class="{ 'is-invalid': submitted && $v.campaign.name.$error }"
                  placeholder="Type Campaign Name"
                  v-model="campaign.name"
                  type="text"
                />
                <div v-if="submitted && !$v.campaign.name.required" class="invalid-feedback">
                  This value is required
                </div>
              </div>
              
              <div class="mb-3 col-md-6">
                <div class="d-flex align-items-center">
                  <label class="form-label small fw-bold mb-0">Schedule Date <span class="text-danger">*</span></label>
                </div>
                <div class="input-group">
                  <span class="input-group-text bg-white border-end-0 px-2 pl-0" style="background-color: #fff; border-right: none;">
                    <i class="bx bx-calendar" style="font-size: 1.2rem;"></i>
                  </span>
                  <flat-pickr
                  v-model="campaign.scheduleDate"
                  :config="dateConfig"
                  placeholder="Select Schedule Date"
                  class="form-control border-start-0"
                  :class="{ 'is-invalid': submitted && $v.campaign.scheduleDate.$error }"
                  ></flat-pickr>
                </div>
                <div v-if="submitted && !$v.campaign.scheduleDate.required" class="invalid-feedback">
                  This value is required
                </div>
              </div>
            </div>
            
            <div class="mb-3">
              <label class="form-label small fw-bold">Sender Name <span class="text-danger">*</span></label>  
              <select 
                class="form-select"
                :class="{ 'is-invalid': submitted && $v.campaign.senderName.$error }"
                v-model="campaign.senderName"
              >
                <option value="">Select Sender Name Here</option>
                <option v-for="option in senderNameOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
                </option>
              </select>
              <div v-if="submitted && !$v.campaign.senderName.required" class="invalid-feedback">
                This value is required
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small fw-bold">Message <span class="text-danger">*</span></label>
              <b-form-textarea
                v-model="campaign.message"
                placeholder="Enter your message here..."
                rows="3"
                max-rows="6"
                :class="{ 'is-invalid': submitted && $v.campaign.message.$error }"
              ></b-form-textarea>
              <div v-if="submitted && !$v.campaign.message.required" class="invalid-feedback">
                This value is required
              </div>
            </div>

            <div class="mb-4">
              <label class="form-label small fw-bold">Upload CSV</label>
              <div class="row">
                <div class="col-sm-10">
                  <b-form-file
                    v-model="massUploadFile"
                    @change="onMassUploadFileChange"
                    class="form-control"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    plain
                  />
                </div>
                <div class="col-sm-2">
                  <button 
                    type="button" 
                    class="btn btn-secondary w-100" 
                    @click="processMassUploadFile" 
                    :disabled="!massUploadFile"
                  >
                    Upload File
                  </button>
                </div>
              </div>
              <div class="col-sm-10 mt-2">
                  <label class="form-label pull-right fs-12 text-primary"><a href="/assets/files/sms-blast-upload-template.xlsx" download>Download Blank Template</a></label>
                  <label class="form-label fs-12">File extension: CSV, xlsx, excel format limit size: 3mb</label>
                </div>
            </div>

            <!-- Send SMS Blast Section -->
            <div class="row align-items-center mb-3">
              <div class="col-md-6">
                <h5 class="card-title mb-0">Send SMS Blast <span class="text-danger">*</span></h5>
              </div>
              <div class="col-md-6 d-flex justify-content-end align-items-center">
                <div class="input-group" style="max-width: 320px;">
                  <span class="input-group-text bg-white border-end-0 px-2" style="background-color: #fff; border-right: none;">
                    <i data-feather="search" class="search-icon"></i>
                  </span>
                  <input 
                    type="text" 
                    class="form-control border-start-0" 
                    placeholder="Search Mobile Number" 
                    v-model="searchMobileNumber"
                  />
                </div>
              </div>
            </div>
            
            <div class="mb-3">
              <div class="mobile-numbers-container" :class="{ 'is-invalid': submitted && mobileNumbersValidation.hasError }">
                <label class="form-label small fw-bold">Mobile Number</label>
                <!-- Scrollable mobile numbers container -->
                  <div v-for="item in filteredMobileNumbers" :key="item.index" class="mb-2">
                    <div class="input-group">
                      <input
                        type="tel"
                        class="form-control mobile-number-input"
                        :class="getPhoneValidationClass(mobileNumbers[item.index], item.index)"
                        v-model="mobileNumbers[item.index]"
                        placeholder="Enter mobile number (e.g., 09123456789)"
                        @input="validatePhoneInput($event, item.index)"
                        @keyup.enter="addMobileNumberRow"
                        maxlength="16"
                      />
                      <button 
                        v-if="mobileNumbers.length > 1"
                        type="button" 
                        class="btn btn-outline-danger"
                        @click="removeMobileNumberRow(item.index)"
                      >
                        <i class="bx bx-trash"></i>
                      </button>
                    </div>
                    <!-- Validation feedback -->
                    <div v-if="mobileNumbers[item.index] && isDuplicatePhoneNumber(mobileNumbers[item.index], item.index)" class="invalid-feedback d-block">
                      <small>This mobile number is already in the list. Please enter a different mobile number.</small>
                    </div>
                    <div v-else-if="mobileNumbers[item.index] && !isValidPhoneNumber(mobileNumbers[item.index])" class="invalid-feedback d-block">
                      <small>Please enter a valid phone number (e.g., 09123456789 or +639123456789)</small>
                    </div>
                  </div>
                  
                  <!-- Show message when no results found -->
                  <div v-if="filteredMobileNumbers.length === 0 && searchMobileNumber.trim()" class="text-center text-muted py-3">
                    <i class="bx bx-search-alt-2 fs-4"></i>
                    <p class="mb-0">No mobile numbers found matching "{{ searchMobileNumber }}"</p>
                  </div>
                  <!-- Row with Add Row button and total count aligned -->
                    <div class="row align-items-center mt-3">
                      <div class="col-md-6">
                        <button
                          type="button" 
                          class="btn btn-link text-primary p-0 add-row-btn"
                          @click="addMobileNumberRow"
                        >
                          <i class="bx bx-plus-circle me-1"></i> Add Row
                        </button>
                      </div>
                      <div class="col-md-6 text-end">
                        <span>Total Mobile Numbers: {{ totalMobileNumbers > 0 ? totalMobileNumbers : "-" }}</span>
                        <span v-if="totalInvalidNumbers > 0" class="text-danger ms-2">
                          ({{ totalInvalidNumbers }} invalid)
                        </span>
                        <span v-if="totalDuplicateNumbers > 0" class="text-warning ms-2">
                          ({{ totalDuplicateNumbers }} duplicate)
                        </span>
                      </div>
                    </div>
                </div>
                <!-- Mobile numbers validation error message -->
                <div v-if="submitted && mobileNumbersValidation.hasError" class="invalid-feedback d-block">
                  {{ mobileNumbersValidation.errorMessage }}
                </div>
            </div>

            <div class="d-flex justify-content-between align-items-center">
              <button 
                type="button" 
                class="btn btn-link text-primary p-0"
                @click="saveDraft"
              >
                SAVE AS DRAFT
              </button>
              
              <div>
                <button 
                  type="button" 
                  class="btn btn-primary me-2"
                  @click="saveCampaign(false)"
                >
                  Save
                </button>
                <button 
                  type="button" 
                  class="btn btn-lightgray"
                  @click="$router.go(-1)"
                >
                  Cancel
                </button>
                
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>
.card {
  border-radius: 8px;
}

.btn-link {
  text-decoration: none;
}

.btn-link:hover {
  text-decoration: underline;
}

.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
}

.form-control:focus {
  border-color: #e73092;
  box-shadow: 0 0 0 0.2rem rgba(231, 48, 146, 0.25);
}

.btn-primary {
  background-color: #e73092;
  border-color: #e73092;
}

.btn-primary:hover,
.btn-primary:focus {
  background-color: #d12576;
  border-color: #d12576;
}

.text-primary {
  color: #e73092 !important;
}

.text-primary:hover {
  color: #d12576 !important;
}

/* Scrollable mobile numbers container */
.mobile-numbers-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 15px;
  background-color: #fafafa;
}

.mobile-numbers-container.is-invalid {
  border-color: #dc3545;
}

/* Custom scrollbar styling */
.mobile-numbers-container::-webkit-scrollbar {
  width: 8px;
}

.mobile-numbers-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.mobile-numbers-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.mobile-numbers-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Firefox scrollbar styling */
.mobile-numbers-container {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

/* Empty state styling */
.mobile-numbers-container .text-muted {
  background-color: transparent;
}

/* Add Row button styling */
.add-row-btn {
  color: #8e44ad !important;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
}

.add-row-btn:hover {
  color: #7d3c98 !important;
  text-decoration: none;
}

.add-row-btn i {
  font-size: 1.1rem;
}

/* Phone number validation styling */
.mobile-number-input.is-valid {
  border-color: #28a745;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.mobile-number-input.is-invalid {
  border-color: #dc3545;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.mobile-number-input.is-valid:focus,
.mobile-number-input.is-invalid:focus {
  box-shadow: 0 0 0 0.2rem rgba(231, 48, 146, 0.25);
  border-color: #e73092;
}
</style>
